import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingBlock } from '@/components/PageHeader';
import { useSession } from '@/lib/auth/session';
import { useQueryClient } from '@tanstack/react-query';

import { useSites } from '@/lib/queries';
import { FixLink as Fix, FixReasons } from '@/pages/site/FixLink';
import { sitePath } from '@/pages/site/siteMenu';
import { WriteHereContext } from '@/pages/site/writeHere';

import { SafeJournalEditor } from './SafeJournalEditor';
import type { JournalType } from './safeJournalApi';
import { TrendSection } from './TrendCharts';

import {
  isLegalLate,
  useSafeBriefing,
  useSafeDashboard,
  type DashboardSite,
  type SafeDashboard,
  type SiteLevel,
} from './safeDashboardApi';

/** [입장] — 이 앱의 현장 화면(세부 메뉴 · DOC-135). 그룹웨어 현장 조회로 가는 길은 그 화면의 좌측 메뉴에 있다. */
function useEnterSite() {
  const navigate = useNavigate();
  return (siteId: string) => navigate(sitePath(siteId));
}

/**
 * 안전 관리 › 안전 현황 (DOC-131) — 안전 관리의 첫 화면.
 *
 * <p>모양은 사용자가 준 기존 안전 서비스 화면(「전체 현장 현황」)을 따른다. 그 화면에서 우리에게 없는 칸은
 * 뺐다(사용자 결정 2026-10-23): 위험성평가 이행률 · 회사 고르개 · 구성원/회사 탭 · [+ 현장추가].
 * 온열은 측정값이 아니라 현장 위치의 예보 최고기온이다.
 *
 * <p>판정 규칙 · 법정 주기는 서버({@code SafeDashboardService})에 있다. 화면은 받은 판정으로 거르고 줄 세운다.
 */
export function SafeDashboardPage() {
  const dashboard = useSafeDashboard();
  const sites = useSites();
  const queryClient = useQueryClient();
  /** 「미작성 · 미실시」 로 연 작성 창 — 대시보드 위에 뜨고, 닫으면 숫자를 새로 받는다(화면은 그대로). */
  const [writing, setWriting] = useState<{ siteId: string; type: JournalType } | null>(null);
  const openWrite = useCallback((siteId: string, type: JournalType) => setWriting({ siteId, type }), []);
  const [view, setView] = useState<View>('list');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('risk');
  /** 표 머리글로 고른 정렬. 있으면 위 고르개보다 앞선다 — 고르개를 바꾸면 풀린다. CSV · 카드도 같은 순서. */
  const [column, setColumn] = useState<ColumnSort | null>(null);

  const data = dashboard.data;
  const shown = useMemo(() => (data ? arrange(data.sites, filter, query, sort, column) : []), [data, filter, query, sort, column]);

  if (dashboard.isPending) return <LoadingBlock />;
  if (!data) {
    return <LoadingBlock />;
  }

  const urgent = data.sites.filter((site) => site.level === 'DANGER' || site.level === 'CAUTION').slice(0, 3);

  return (
    <WriteHereContext.Provider value={openWrite}>
    <div className="space-y-5">
      <style>{STYLES}</style>
      <h1 style={{ fontSize: '28px', fontWeight: 800, letterSpacing: '-0.02em', margin: '4px 0 0' }}>대시보드</h1>

      <Briefing
        urgent={urgent[0] ?? null}
        onLate={() => {
          setFilter('LEGAL');
          setView('list');
        }}
        onAnalysis={() => setView('analysis')}
      />

      <div className="flex justify-end">
        <button type="button" className="sd-link" onClick={() => void dashboard.refetch()} disabled={dashboard.isFetching}>
          ↻ 새로고침 · {dashboard.isFetching ? '불러오는 중' : `${ago(data.generatedAt)} 업데이트`}
        </button>
      </div>

      <StatCards data={data} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 style={{ fontSize: '24px', fontWeight: 800, margin: 0 }}>현장 현황</h2>
          <span style={{ fontSize: '15px', fontWeight: 600 }}>
            표시 {shown.length} / 전체 {data.summary.totalSites}곳
          </span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--danger)' }}>· 위험 {data.summary.levels.DANGER}</span>
          <button type="button" className="sd-link" style={{ textDecoration: 'underline' }} onClick={() => setFilter('OPEN')}>
            · 미조치 {data.summary.openItems}건
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary sd-tall" onClick={() => exportCsv(shown, data.today)}>
            CSV 내보내기 (표시 {shown.length}곳)
          </button>
          <div className="sd-toggle">
            {VIEWS.map((one) => (
              <button key={one.key} type="button" className={view === one.key ? 'on' : ''} onClick={() => setView(one.key)}>
                {one.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view !== 'analysis' && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="sd-chips">
              {FILTERS.map((one) => (
                <button key={one.key} type="button" className={filter === one.key ? 'on' : ''} onClick={() => setFilter(one.key)}>
                  {one.dot && <span className="sd-dot" style={{ background: one.dot }} />}
                  {one.label} <span className="sd-count">{countOf(data.sites, one.key)}</span>
                </button>
              ))}
            </div>
            <input
              className="input sd-tall"
              style={{ width: '200px' }}
              placeholder="현장명 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select className="input sd-tall" style={{ width: '170px', marginLeft: 'auto' }} value={sort} onChange={(e) => {
              setSort(e.target.value as Sort);
              setColumn(null);
            }}>
              {SORTS.map((one) => (
                <option key={one.key} value={one.key}>
                  {one.label}
                </option>
              ))}
            </select>
          </div>

          {urgent.length > 0 && (
            <div className="card sd-urgent">
              <strong>가장 급한 현장</strong>
              {urgent.map((site, index) => (
                <span key={site.siteId}>
                  {index + 1} <b>{site.siteName}</b>{' '}
                  <span style={{ color: 'var(--danger)' }}>
                    <FixReasons siteId={site.siteId} reasons={site.reasons.slice(0, 2)} />
                  </span>
                </span>
              ))}
              <button type="button" className="sd-link" style={{ marginLeft: 'auto' }} onClick={() => setView('analysis')}>
                분석 보기 ›
              </button>
            </div>
          )}

          {shown.length === 0 ? (
            <div className="card p-8 text-center" style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              해당하는 현장이 없습니다.
            </div>
          ) : view === 'list' ? (
            <SiteTable
              sites={shown}
              column={column}
              onSort={(key) => setColumn((now) => (now?.key === key ? { key, dir: now.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))}
            />
          ) : (
            <SiteCards sites={shown} />
          )}
        </>
      )}

      {view === 'analysis' && <Analysis data={data} />}

      {writing && (
        <SafeJournalEditor
          key={`${writing.siteId}:${writing.type}`}
          type={writing.type}
          journalId={null}
          defaultSite={writing.siteId}
          sites={sites.data ?? []}
          onClose={() => {
            setWriting(null);
            void queryClient.invalidateQueries({ queryKey: ['safe-dashboard'] });
          }}
        />
      )}
    </div>
    </WriteHereContext.Provider>
  );
}

// ── 비서 한 줄 ───────────────────────────────────────────────────────────

/**
 * AI Agent 한 줄(사용자 결정 2026-10-23). 기다리는 동안 · 쓸 수 없을 때 모두 「불러오는 중입니다」 —
 * 사유는 화면에 쓰지 않는다. 버튼 셋은 AI 없이 데이터로 선다.
 */
function Briefing({
  urgent,
  onLate,
  onAnalysis,
}: {
  urgent: DashboardSite | null;
  onLate: () => void;
  onAnalysis: () => void;
}) {
  const me = useSession().data?.user?.name ?? null;
  const enterSite = useEnterSite();
  const briefing = useSafeBriefing(true);
  const text = briefing.data?.available && briefing.data.text ? `${me ? `${me}님, ` : ''}${briefing.data.text}` : '불러오는 중입니다';
  return (
    <div className="sd-brief">
      <div className="sd-avatar" aria-hidden>
        AI
      </div>
      <div className="space-y-3" style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13px' }}>
          <b style={{ fontSize: '15px' }}>AI Agent</b> <span style={{ color: 'var(--text-muted)' }}>안전 현황</span>
        </div>
        <div style={{ fontSize: '15px' }}>{text}</div>
        <div className="flex gap-2 flex-wrap">
          {urgent && (
            <button type="button" className="sd-pill" onClick={() => enterSite(urgent.siteId)}>
              {urgent.siteName} 입장하기 ›
            </button>
          )}
          <button type="button" className="sd-pill" onClick={onLate}>
            지연 현장 확인하기 ›
          </button>
          <button type="button" className="sd-pill" onClick={onAnalysis}>
            분석 보기 ›
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 카드 셋 ──────────────────────────────────────────────────────────────

function StatCards({ data }: { data: SafeDashboard }) {
  const s = data.summary;
  return (
    <div className="sd-cards">
      <Stat label="오늘 TBM 작성" value={s.tbmSites} unit={`/${s.activeSites}`} note="진행 현장 기준 · 안전보건일지 · 협력사 포털 TBM" />
      <Stat
        label="온열 경고·위험"
        value={s.heatWarn + s.heatDanger}
        unit="현장"
        note={`진행 ${s.activeSites}곳 중 · 오늘 기온 ${s.heatKnown}곳 (위험 ${s.heatDanger} · 경고 ${s.heatWarn})`}
      />
      <Stat
        label="법정점검 지연·미실시"
        value={s.legalSites}
        unit="현장"
        note={`순회 ${s.patrolOverdue}곳 · 합동 ${s.jointOverdue}곳 — 시스템 기록 기준(§80·§82)`}
        warn={s.legalSites > 0}
      />
    </div>
  );
}

function Stat({ label, value, unit, note, warn }: { label: string; value: number; unit: string; note: string; warn?: boolean }) {
  return (
    <div className="card sd-stat">
      <div style={{ fontSize: '14px' }}>{label}</div>
      <div style={{ margin: '6px 0' }}>
        <span style={{ fontSize: '36px', fontWeight: 800 }}>{value}</span>
        <span style={{ fontSize: '16px', marginLeft: '4px' }}>{unit}</span>
      </div>
      <div style={{ fontSize: '12px', color: warn ? '#b45309' : 'var(--text-secondary)' }}>{note}</div>
    </div>
  );
}

// ── 목록 · 카드 ──────────────────────────────────────────────────────────

function SiteTable({
  sites,
  column,
  onSort,
}: {
  sites: DashboardSite[];
  column: ColumnSort | null;
  onSort: (key: ColumnKey) => void;
}) {
  const enterSite = useEnterSite();
  const th = (key: ColumnKey, label: string, style?: CSSProperties) => (
    <th className="sd-sort" style={style} onClick={() => onSort(key)}>
      {label}
      <span className="sd-sort-mark">{column?.key === key ? (column.dir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  );
  return (
    <div className="card p-0" style={{ overflowX: 'auto' }}>
      <table className="sd-table">
        <thead>
          <tr>
            <th style={{ width: '56px' }}>No.</th>
            {th('name', '현장명', { textAlign: 'left' })}
            {th('tbm', '오늘 TBM')}
            {th('heat', '온열')}
            {th('legal', '법정점검')}
            {th('open', '미조치')}
            {th('workers', '인원')}
            {th('lead', '총괄')}
            {th('activity', '최근활동')}
            <th>이동</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site, index) => (
            <tr key={site.siteId}>
              <td>{index + 1}</td>
              <td style={{ textAlign: 'left' }}>
                <div className="flex items-center gap-2">
                  <LevelBadge level={site.level} />
                  <b style={{ fontSize: '15px' }}>{site.siteName}</b>
                </div>
                {site.reasons.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '3px' }}>
                    ○ <FixReasons siteId={site.siteId} reasons={site.reasons} />
                  </div>
                )}
              </td>
              <td>{tbmText(site)}</td>
              <td>
                <HeatText site={site} />
              </td>
              <td>
                <LegalText site={site} />
              </td>
              <td>{site.level === 'ENDED' ? '—' : openOf(site) > 0 ? <Fix siteId={site.siteId} text="미조치">{openOf(site)}</Fix> : 0}</td>
              <td>{site.workers}명</td>
              <td>{site.leadName ?? '—'}</td>
              <td>{activity(site.lastActivity)}</td>
              <td>
                <button type="button" className="sd-enter" onClick={() => enterSite(site.siteId)}>
                  입장 ›
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SiteCards({ sites }: { sites: DashboardSite[] }) {
  const enterSite = useEnterSite();
  return (
    <div className="sd-site-cards">
      {sites.map((site) => (
        <div key={site.siteId} className="card sd-site-card">
          <div className="flex items-center gap-2">
            <LevelBadge level={site.level} />
            <b style={{ fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{site.siteName}</b>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', minHeight: '18px' }}>
            <FixReasons siteId={site.siteId} reasons={site.reasons} />
          </div>
          <dl>
            <dt>오늘 TBM</dt>
            <dd>{tbmText(site)}</dd>
            <dt>온열</dt>
            <dd>
              <HeatText site={site} />
            </dd>
            <dt>법정점검</dt>
            <dd>
              <LegalText site={site} />
            </dd>
            <dt>미조치</dt>
            <dd>{site.level === 'ENDED' ? '—' : openOf(site) === 0 ? '0건' : <Fix siteId={site.siteId} text="미조치">{`${openOf(site)}건`}</Fix>}</dd>
            <dt>인원 · 총괄</dt>
            <dd>
              {site.workers}명 · {site.leadName ?? '—'}
            </dd>
            <dt>최근활동</dt>
            <dd>{activity(site.lastActivity)}</dd>
          </dl>
          <button type="button" className="sd-enter" style={{ alignSelf: 'flex-end' }} onClick={() => enterSite(site.siteId)}>
            입장 ›
          </button>
        </div>
      ))}
    </div>
  );
}

function LevelBadge({ level }: { level: SiteLevel }) {
  const { label, badge } = LEVELS[level];
  return <span className={`badge ${badge}`}>{label}</span>;
}

function HeatText({ site }: { site: DashboardSite }) {
  if (site.maxTemp === null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = site.heat === '위험' ? 'var(--danger)' : site.heat === '경고' ? '#b45309' : undefined;
  return (
    <span style={{ color, fontWeight: site.heat ? 700 : 400 }}>
      최고 {site.maxTemp}℃{site.heat ? ` · ${site.heat}` : ''}
    </span>
  );
}

function LegalText({ site }: { site: DashboardSite }) {
  if (site.level === 'ENDED') return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const lines = [
    site.patrolLate === null ? '순회점검 미실시' : site.patrolLate > 0 ? `순회점검 지난 ${site.patrolLate}일` : null,
    site.jointLate === null ? '합동점검 미실시' : site.jointLate > 0 ? `합동점검 지난 ${site.jointLate}일` : null,
  ].filter(Boolean);
  if (lines.length === 0) return <span style={{ color: '#15803d' }}>준수</span>;
  return (
    <div style={{ color: 'var(--danger)', fontSize: '12px', fontWeight: 600, lineHeight: 1.5 }}>
      {lines.map((line) => (
        <div key={line}>
          <Fix siteId={site.siteId} text={line!} />
        </div>
      ))}
    </div>
  );
}

// ── 분석 ─────────────────────────────────────────────────────────────────

/**
 * 분석 보기 — 판정별 현장 수, 최근 14일 추이(TBM 작성 현장 · 순회점검 건수, 두 척도라 따로 그린다),
 * 미조치가 많은 현장. 차트 라이브러리가 없어 SVG 로 그린다.
 */
function Analysis({ data }: { data: SafeDashboard }) {
  const levels = (Object.keys(LEVELS) as SiteLevel[]).map((level) => ({
    label: LEVELS[level].label,
    value: data.summary.levels[level],
    color: LEVELS[level].color,
  }));
  const open = data.sites
    .filter((site) => site.level !== 'ENDED' && openOf(site) > 0)
    .sort((a, b) => openOf(b) - openOf(a))
    .slice(0, 10)
    .map((site) => ({ label: site.siteName, value: openOf(site), color: ACCENT }));
  // 미실시(기록 없음)는 날 수가 없다 — 가장 늦은 현장보다 위에, 끝까지 찬 막대로 「미실시」 라고 쓴다.
  const lateRows = data.sites
    .filter(isLegalLate)
    .map((site) => ({ label: site.siteName, days: Math.max(site.patrolLate ?? 0, site.jointLate ?? 0), never: site.patrolLate === null || site.jointLate === null }))
    .sort((a, b) => Number(b.never) - Number(a.never) || b.days - a.days)
    .slice(0, 10);
  const lateMax = Math.max(1, ...lateRows.map((row) => row.days));
  const late = lateRows.map((row) => ({
    label: row.label,
    value: row.never ? lateMax : row.days,
    color: '#dc2626',
    text: row.never ? '미실시' : `${row.days}일`,
  }));

  return (
    <div className="sd-analysis">
      <ChartCard title="판정별 현장 수">
        <Bars rows={levels} unit="곳" />
      </ChartCard>
      <ChartCard title="최근 14일 · TBM 작성 현장 수">
        <Line points={data.daily.map((d) => ({ day: d.day, value: d.tbmSites }))} unit="곳" />
      </ChartCard>
      <ChartCard title="최근 14일 · 순회점검 건수">
        <Line points={data.daily.map((d) => ({ day: d.day, value: d.patrols }))} unit="건" />
      </ChartCard>
      <ChartCard title="미조치가 많은 현장">
        {open.length === 0 ? <Empty /> : <Bars rows={open} unit="건" />}
      </ChartCard>
      <ChartCard title="법정점검이 가장 늦은 현장">
        {late.length === 0 ? <Empty /> : <Bars rows={late} unit="일" />}
      </ChartCard>
      {/* 연도별 추이 — 옛 안전보건관리시스템 + 우리 기록(사용자 결정 2026-10-28). 격자 두 칸을 다 쓴다. */}
      <TrendSection />
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>{title}</div>
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>없음</div>;
}

/** 가로 막대. 막대 끝에 값을 쓰고, 막대에 올리면 같은 값이 뜬다(title). {@code text} 가 있으면 값 대신 그 글을 쓴다. */
function Bars({ rows, unit }: { rows: Array<{ label: string; value: number; color: string; text?: string }>; unit: string }) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const shown = row.text ?? `${row.value}${unit}`;
        return (
          <div key={row.label} className="sd-bar" title={`${row.label} · ${shown}`}>
            <span className="sd-bar-label">{row.label}</span>
            <span className="sd-bar-track">
              <span style={{ width: `${(row.value / max) * 100}%`, background: row.color }} />
            </span>
            <span className="sd-bar-value">{shown}</span>
          </div>
        );
      })}
    </div>
  );
}

/** 하루 한 점의 선. 올리면 그날의 세로줄과 값이 뜬다. */
function Line({ points, unit }: { points: Array<{ day: string; value: number }>; unit: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 520;
  const H = 180;
  const pad = { l: 28, r: 12, t: 12, b: 24 };
  const max = Math.max(1, ...points.map((p) => p.value));
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / Math.max(1, points.length - 1);
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.value)}`).join(' ');
  const at = hover === null ? null : points[hover];
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        const box = e.currentTarget.getBoundingClientRect();
        const px = ((e.clientX - box.left) / box.width) * W;
        const i = Math.round(((px - pad.l) / (W - pad.l - pad.r)) * (points.length - 1));
        setHover(Math.min(points.length - 1, Math.max(0, i)));
      }}
    >
      {/* 눈금은 정수에만 — 가운데 눈금은 최댓값이 짝수일 때만 선다(1.5 자리에 「2」 를 쓰지 않게). */}
      {(max % 2 === 0 ? [0, max / 2, max] : [0, max]).map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth={1} />
          <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize={10} fill="var(--text-muted)">
            {v}
          </text>
        </g>
      ))}
      {points.map((p, i) =>
        i % 3 === 0 || i === points.length - 1 ? (
          <text key={p.day} x={x(i)} y={H - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
            {p.day.slice(5).replace('-', '.')}
          </text>
        ) : null,
      )}
      <path d={path} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />
      {hover !== null && at && (
        <g>
          <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--text-muted)" strokeWidth={1} strokeDasharray="3 3" />
          <circle cx={x(hover)} cy={y(at.value)} r={4.5} fill={ACCENT} stroke="#fff" strokeWidth={2} />
          <text
            x={Math.min(W - pad.r - 4, Math.max(pad.l + 4, x(hover)))}
            y={pad.t + 10}
            textAnchor={x(hover) > W - 90 ? 'end' : x(hover) < 90 ? 'start' : 'middle'}
            fontSize={11}
            fontWeight={700}
            fill="var(--text-primary)"
          >
            {at.day.slice(5).replace('-', '.')} · {at.value}
            {unit}
          </text>
        </g>
      )}
    </svg>
  );
}

// ── 거르기 · 정렬 · 내보내기 ─────────────────────────────────────────────

type View = 'list' | 'card' | 'analysis';
type Filter = 'ALL' | SiteLevel | 'HEAT' | 'LEGAL' | 'OPEN';
type Sort = 'risk' | 'name' | 'activity' | 'workers';
type ColumnKey = 'name' | 'tbm' | 'heat' | 'legal' | 'open' | 'workers' | 'lead' | 'activity';
type ColumnSort = { key: ColumnKey; dir: 'asc' | 'desc' };

/**
 * 머리글 정렬의 값. null(종료 현장의 TBM · 점검 · 미조치, 기온 없음, 총괄 없음, 활동 없음)은 방향과 상관없이 맨 아래.
 * 법정점검은 더 늦은 쪽의 지난 날 수, 미실시는 가장 늦은 것으로 친다. TBM 미작성은 서명 0명보다 아래.
 */
const COLUMN_VALUE: Record<ColumnKey, (site: DashboardSite) => number | string | null> = {
  name: (site) => site.siteName,
  tbm: (site) => (site.level === 'ENDED' ? null : site.tbmToday ? site.tbmSigners : -1),
  heat: (site) => site.maxTemp,
  legal: (site) =>
    site.level === 'ENDED' ? null : site.patrolLate === null || site.jointLate === null ? Number.MAX_SAFE_INTEGER : Math.max(site.patrolLate, site.jointLate),
  open: (site) => (site.level === 'ENDED' ? null : openOf(site)),
  workers: (site) => site.workers,
  lead: (site) => site.leadName,
  activity: (site) => site.lastActivity,
};

function compareColumn(a: DashboardSite, b: DashboardSite, column: ColumnSort): number {
  const x = COLUMN_VALUE[column.key](a);
  const y = COLUMN_VALUE[column.key](b);
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'ko');
  return column.dir === 'asc' ? order : -order;
}

const ACCENT = '#2563eb';

const LEVELS: Record<SiteLevel, { label: string; badge: string; color: string }> = {
  DANGER: { label: '위험', badge: 'badge-red', color: '#dc2626' },
  CAUTION: { label: '주의', badge: 'badge-yellow', color: '#b45309' },
  GOOD: { label: '양호', badge: 'badge-green', color: '#15803d' },
  ENDED: { label: '종료', badge: 'badge-gray', color: '#6b7280' },
};

const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'list', label: '목록' },
  { key: 'card', label: '카드' },
  { key: 'analysis', label: '분석' },
];

const FILTERS: Array<{ key: Filter; label: string; dot?: string }> = [
  { key: 'ALL', label: '전체' },
  { key: 'DANGER', label: '위험', dot: LEVELS.DANGER.color },
  { key: 'CAUTION', label: '주의', dot: LEVELS.CAUTION.color },
  { key: 'GOOD', label: '양호', dot: LEVELS.GOOD.color },
  { key: 'HEAT', label: '온열 경고·위험', dot: '#ea580c' },
  { key: 'LEGAL', label: '법정 미준수', dot: '#dc2626' },
  { key: 'ENDED', label: '종료', dot: LEVELS.ENDED.color },
];

const SORTS: Array<{ key: Sort; label: string }> = [
  { key: 'risk', label: '위험 우선' },
  { key: 'name', label: '현장명' },
  { key: 'activity', label: '최근활동' },
  { key: 'workers', label: '인원 많은 순' },
];

function matches(site: DashboardSite, filter: Filter): boolean {
  switch (filter) {
    case 'ALL':
      return true;
    case 'HEAT':
      return site.heat !== null;
    case 'LEGAL':
      return isLegalLate(site);
    case 'OPEN':
      return site.level !== 'ENDED' && openOf(site) > 0;
    default:
      return site.level === filter;
  }
}

function countOf(sites: DashboardSite[], filter: Filter): number {
  return sites.filter((site) => matches(site, filter)).length;
}

function arrange(sites: DashboardSite[], filter: Filter, query: string, sort: Sort, column: ColumnSort | null): DashboardSite[] {
  const q = query.trim().toLowerCase();
  const rows = sites.filter((site) => matches(site, filter) && (!q || site.siteName.toLowerCase().includes(q)));
  const by: Record<Sort, (a: DashboardSite, b: DashboardSite) => number> = {
    risk: (a, b) => b.score - a.score,
    name: (a, b) => a.siteName.localeCompare(b.siteName, 'ko'),
    activity: (a, b) => (b.lastActivity ?? '').localeCompare(a.lastActivity ?? ''),
    workers: (a, b) => b.workers - a.workers,
  };
  // 머리글 정렬이 같으면 고르개 순서로 잇는다.
  return [...rows].sort((a, b) => (column ? compareColumn(a, b, column) : 0) || by[sort](a, b));
}

function openOf(site: DashboardSite): number {
  return site.openActions + site.openChecks;
}

function tbmText(site: DashboardSite): ReactNode {
  if (site.level === 'ENDED') return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (!site.tbmToday)
    return (
      <span style={{ color: '#b45309' }}>
        <Fix siteId={site.siteId} text="TBM 미작성">미작성</Fix>
      </span>
    );
  return `${site.tbmSigners}/${site.workers}`;
}

function activity(value: string | null): string {
  if (!value) return '—';
  const at = new Date(value);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  if (at.toDateString() === now.toDateString()) return `오늘 ${pad(at.getHours())}:${pad(at.getMinutes())}`;
  return `${pad(at.getMonth() + 1)}.${pad(at.getDate())}`;
}

function ago(value: string): string {
  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  return `${Math.floor(minutes / 60)}시간 전`;
}

/** 보이는 줄 그대로 CSV 로 내려받는다. 엑셀이 한글을 읽도록 BOM 을 붙인다. */
function exportCsv(sites: DashboardSite[], today: string) {
  const head = ['No', '현장명', '판정', '사유', '오늘 TBM', 'TBM 서명', '인원', '최고기온(예보)', '온열', '최근 순회점검', '최근 합동점검', '미조치', '총괄', '최근활동'];
  const rows = sites.map((site, index) => [
    index + 1,
    site.siteName,
    LEVELS[site.level].label,
    site.reasons.join(' · '),
    site.tbmToday ? '작성' : '미작성',
    site.tbmSigners,
    site.workers,
    site.maxTemp ?? '',
    site.heat ?? '',
    site.lastPatrol ?? '',
    site.lastJoint ?? '',
    openOf(site),
    site.leadName ?? '',
    site.lastActivity ?? '',
  ]);
  const cell = (value: string | number) => {
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = [head, ...rows].map((row) => row.map(cell).join(',')).join('\r\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  link.download = `안전현황_${today}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

const STYLES = `
.sd-link { background: none; border: none; cursor: pointer; font-size: 13px; color: var(--text-secondary); padding: 0; }
.sd-tall { min-height: 40px; }
.sd-brief { display: flex; gap: 16px; padding: 18px 20px; border-radius: 14px; background: var(--bg-hover, #f1f5f9); }
.sd-avatar { flex: none; width: 40px; height: 40px; border-radius: 50%; background: #dbeafe; color: #1d4ed8; font-weight: 800; font-size: 13px; display: flex; align-items: center; justify-content: center; }
.sd-pill { border: 1px solid var(--border); background: var(--bg-card, #fff); border-radius: 999px; padding: 6px 14px; font-size: 13px; font-weight: 600; cursor: pointer; }
.sd-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.sd-stat { padding: 18px 20px; border-radius: 14px; }
.sd-toggle { display: inline-flex; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.sd-toggle button { padding: 9px 16px; font-size: 13px; background: var(--bg-card, #fff); border: none; cursor: pointer; }
.sd-toggle button.on { background: var(--text-primary); color: #fff; font-weight: 700; }
.sd-chips { display: inline-flex; gap: 4px; padding: 4px; border-radius: 10px; background: var(--bg-hover, #f1f5f9); flex-wrap: wrap; }
.sd-chips button { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px; border: none; background: transparent; font-size: 13px; cursor: pointer; }
.sd-chips button.on { background: var(--bg-card, #fff); font-weight: 700; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.sd-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.sd-count { color: var(--text-muted); }
.sd-urgent { display: flex; align-items: center; gap: 18px; padding: 12px 16px; font-size: 13px; flex-wrap: wrap; }
.sd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
/* 폰(앱의 기준 · DOC-140) — 칸을 줄이지 않고 표를 가로로 민다. 줄이면 현장 이름이 한 글자씩 세로로 쪼개진다. */
@media (max-width: 640px) { .sd-table { min-width: 760px; } }
.sd-table th { padding: 12px 10px; font-weight: 600; color: var(--text-secondary); border-bottom: 1px solid var(--text-primary); text-align: center; white-space: nowrap; }
.sd-table th.sd-sort { cursor: pointer; user-select: none; }
.sd-table th.sd-sort:hover { color: var(--text-primary); }
.sd-sort-mark { display: inline-block; width: 12px; margin-left: 3px; font-size: 10px; }
.sd-table td { padding: 12px 10px; border-bottom: 1px solid var(--border); text-align: center; vertical-align: middle; }
.sd-enter { padding: 8px 14px; border-radius: 8px; border: none; background: var(--bg-hover, #f1f5f9); font-weight: 700; cursor: pointer; white-space: nowrap; }
.sd-site-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.sd-site-card { display: flex; flex-direction: column; gap: 8px; padding: 16px; border-radius: 14px; }
.sd-site-card dl { display: grid; grid-template-columns: 76px 1fr; gap: 4px 8px; font-size: 13px; margin: 0; }
.sd-site-card dt { color: var(--text-muted); }
.sd-site-card dd { margin: 0; }
.sd-analysis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.sd-bar { display: grid; grid-template-columns: 160px 1fr 64px; align-items: center; gap: 10px; font-size: 13px; }
.sd-bar-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sd-bar-track { height: 14px; background: var(--bg-hover, #f1f5f9); border-radius: 4px; overflow: hidden; }
.sd-bar-track span { display: block; height: 100%; border-radius: 0 4px 4px 0; }
.sd-bar-value { text-align: right; font-weight: 600; }
@media (max-width: 900px) {
  .sd-cards, .sd-analysis { grid-template-columns: minmax(0, 1fr); }
}
`;
